import { createContext, useContext, useCallback, type FC, type PropsWithChildren } from 'react';
import type { AlgoliaProductAttributes } from '../AlgoliaProductAttributes';

export interface GoogleAnalyticsItem {
    item_id: string;
    item_name: string;
    affiliation: string;
    index: number;
    item_brand: string;
    item_category: string;
    item_list_name: string;
    item_variant: string;
    price: number;
}

export interface ViewItemListEventData {
    event: 'view_item_list';
    ecommerce: {
        category_id: string;
        category_name: string;
        currency: string;
        items: GoogleAnalyticsItem[];
    };
}

export interface SearchEventData {
    event: 'search';
    search_term: string;
    items_found: number;
}

export type AnalyticsEvent = ViewItemListEventData | SearchEventData;

interface AnalyticsContextType {
    sendEvent: (eventData: AnalyticsEvent) => void;
    trackProductInteraction: (item: AlgoliaProductAttributes, action: string, context?: string) => void;
    trackViewItemList: (items: AlgoliaProductAttributes[], isSearch?: boolean) => void;
    trackSearch: (searchTerm: string, itemsFound: number) => void;
}

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
    const sendEvent = useCallback((eventData: AnalyticsEvent) => {
        if (typeof window !== 'undefined' && window.dataLayer) {
            if (eventData.event === 'view_item_list') {
                window.dataLayer.push({ ecommerce: null });
            }
            window.dataLayer.push(eventData);
        }
    }, []);

    const extractPriceFromDOM = useCallback((item: AlgoliaProductAttributes): number => {
        const itemElement = document.querySelector(`#item_${item.objectID}, [data-itemno="${item.itemNo}"]`);
        let domPrice = 0;

        if (itemElement) {
            const priceElement = itemElement.querySelector(
                '.algoliaPrices .basePrice, .prices .basePrice, .algoliaPrices .itemPrice, .algoliaPrices .price, .itemPrice, .price, [data-price]'
            );
            if (priceElement) {
                const priceText = priceElement.textContent || priceElement.getAttribute('data-price') || '';
                const priceMatch = priceText.match(/[\d.,]+/);
                domPrice = priceMatch ? parseFloat(priceMatch[0].replace(',', '.')) : 0;
            }
        }

        return domPrice || item.price || 0;
    }, []);

    const trackProductInteraction = useCallback((item: AlgoliaProductAttributes, action: string, context?: string) => {
        console.log('Product interaction:', { item: item.objectID, action, context });
    }, []);

    const trackViewItemList = useCallback((items: AlgoliaProductAttributes[], isSearch = false) => {
        if (!items.length) return;

        const itemsData: GoogleAnalyticsItem[] = items.map((item, index) => ({
            item_id: item.objectID,
            item_name: item.description || item.name || '',
            affiliation: 'shop',
            index: index + 1,
            item_brand: item.brand || '',
            item_category: item.category || '',
            item_list_name: isSearch ? 'search_results' : 'category',
            item_variant: item.type || '',
            price: extractPriceFromDOM(item)
        }));

        const eventData: ViewItemListEventData = {
            event: 'view_item_list',
            ecommerce: {
                category_id: '',
                category_name: '',
                currency: 'CHF',
                items: itemsData
            }
        };

        sendEvent(eventData);
    }, [extractPriceFromDOM, sendEvent]);

    const trackSearch = useCallback((searchTerm: string, itemsFound: number) => {
        const eventData: SearchEventData = {
            event: 'search',
            search_term: searchTerm.trim(),
            items_found: itemsFound
        };

        sendEvent(eventData);
    }, [sendEvent]);

    return (
        <AnalyticsContext.Provider value={{
            sendEvent,
            trackProductInteraction,
            trackViewItemList,
            trackSearch
        }}>
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};